import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { t } from "@/lib/i18n";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, MessageCircle, CalendarDays, Bell, BellOff, Headphones } from "lucide-react";
import { InstallBanner } from "@/components/InstallGuide";
import { Skeleton } from "@/components/ui/skeleton";
import { isPushSupported, subscribeToPush, currentSubscription } from "@/lib/push";

// Curated daily-rotating Verse of the Day imagery — landscape, spiritual, calm.
// Same image for every user on the same date (deterministic by day-of-year).
const VOTD_IMAGES = [
  "https://images.unsplash.com/photo-1486707471592-8e7eb7e36f78?auto=format&fit=crop&w=1600&q=85", // misty forest
  "https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?auto=format&fit=crop&w=1600&q=85", // mountain sunrise
  "https://images.unsplash.com/photo-1444080748397-f442aa95c3e5?auto=format&fit=crop&w=1600&q=85", // golden field
  "https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?auto=format&fit=crop&w=1600&q=85", // lake reflection
  "https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?auto=format&fit=crop&w=1600&q=85", // forest path
  "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=1600&q=85", // foggy mountains
  "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1600&q=85", // mountain lake
  "https://images.unsplash.com/photo-1472214103451-9374bd1c798e?auto=format&fit=crop&w=1600&q=85", // field sunrise
  "https://images.unsplash.com/photo-1418065460487-3e41a6c84dc5?auto=format&fit=crop&w=1600&q=85", // meadow warm light
  "https://images.unsplash.com/photo-1439853949127-fa647821eba0?auto=format&fit=crop&w=1600&q=85", // ocean cliff
  "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&w=1600&q=85", // sun through trees
  "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=1600&q=85", // forest sunrays
  "https://images.unsplash.com/photo-1490604001847-b712b0c2f967?auto=format&fit=crop&w=1600&q=85", // green valley
  "https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=1600&q=85", // stars mountain
  "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1600&q=85", // desert sunset
  "https://images.unsplash.com/photo-1490604001847-b712b0c2f967?auto=format&fit=crop&w=1600&q=85", // green valley 2
  "https://images.unsplash.com/photo-1454496522488-7a8e488e8606?auto=format&fit=crop&w=1600&q=85", // pine forest
  "https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&w=1600&q=85", // lake mountains
  "https://images.unsplash.com/photo-1502082553048-f009c37129b9?auto=format&fit=crop&w=1600&q=85", // dawn field
  "https://images.unsplash.com/photo-1531366936337-7c912a4589a7?auto=format&fit=crop&w=1600&q=85", // aurora sky (replacement, verified)
  "https://images.unsplash.com/photo-1477346611705-65d1883cee1e?auto=format&fit=crop&w=1600&q=85", // river dawn
  "https://images.unsplash.com/photo-1505142468610-359e7d316be0?auto=format&fit=crop&w=1600&q=85", // beach horizon
  "https://images.unsplash.com/photo-1483347756197-71ef80e95f73?auto=format&fit=crop&w=1600&q=85", // hills mist
  "https://images.unsplash.com/photo-1490682143684-14369e18dce8?auto=format&fit=crop&w=1600&q=85", // sky clouds
  "https://images.unsplash.com/photo-1518623489648-a173ef7824f3?auto=format&fit=crop&w=1600&q=85", // starry night
  "https://images.unsplash.com/photo-1508739773434-c26b3d09e071?auto=format&fit=crop&w=1600&q=85", // pines fog
  "https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07?auto=format&fit=crop&w=1600&q=85", // wildflowers sunset
  "https://images.unsplash.com/photo-1493246507139-91e8fad9978e?auto=format&fit=crop&w=1600&q=85", // lake sunset
  "https://images.unsplash.com/photo-1418065460487-3e41a6c84dc5?auto=format&fit=crop&w=1600&q=85", // meadow 2
  "https://images.unsplash.com/photo-1418065460487-3e41a6c84dc5?auto=format&fit=crop&w=1600&q=85", // fallback
  "https://images.unsplash.com/photo-1518098268026-4e89f1a2cd8e?auto=format&fit=crop&w=1600&q=85", // beach dawn
];

function dailyImage(images) {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = now - start;
  const dayOfYear = Math.floor(diff / (1000 * 60 * 60 * 24));
  return images[dayOfYear % images.length];
}

const VOTD_IMG = dailyImage(VOTD_IMAGES);
const READING_IMG = "https://images.unsplash.com/photo-1504052434569-70ad5836ab65?auto=format&fit=crop&w=1200&q=80";
const COMMUNITY_IMG = "https://images.unsplash.com/photo-1511632765486-a01980e01a18?auto=format&fit=crop&w=1200&q=80";

export default function Home() {
  const { lang, version, user } = useApp();
  const navigate = useNavigate();
  const [votd, setVotd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [todayReading, setTodayReading] = useState(null); // { plan, day, book, chapter, book_name }
  const [notifPerm, setNotifPerm] = useState(() =>
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    (async () => {
      if (!isPushSupported()) return;
      const sub = await currentSubscription();
      setPushSubscribed(!!sub);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/bible/verse-of-day`, { params: { translation: version } });
        setVotd(res.data);
      } catch {
        setVotd(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [version, lang]);

  const [planList, setPlanList] = useState([]);

  // Determine "today's reading" — the earliest unread day across any plan the user has started
  useEffect(() => {
    (async () => {
      try {
        const plansRes = await api.get("/plans");
        const plans = plansRes.data;
        setPlanList(plans);
        let best = null;
        for (const p of plans) {
          const prog = (await api.get(`/plans/${p.plan_id}/progress`)).data;
          const done = new Set(prog.completed_days || []);
          if (done.size === 0) continue; // user hasn't started this plan
          if (done.size >= p.duration_days) continue; // already finished
          const next = p.readings.find((r) => !done.has(r.day));
          if (next && (!best || done.size / p.duration_days > best.progress)) {
            best = {
              plan: p,
              day: next.day,
              book: next.book,
              chapter: next.chapter,
              progress: done.size / p.duration_days,
            };
          }
        }
        setTodayReading(best);
        // Fire browser notification if permission granted and we haven't reminded today.
        // Uses the Service Worker so the click routes to the right URL (devotional or reader).
        if (best && notifPerm === "granted") {
          const key = "lumen_last_notif";
          const today = new Date().toDateString();
          if (localStorage.getItem(key) !== today) {
            const title = lang === "es" ? "Tu lectura de hoy" : "Today's reading";
            const body = `${lang === "es" ? "Día" : "Day"} ${best.day} — ${lang === "es" ? best.plan.name_es : best.plan.name_en}`;
            const url = best.plan.is_devotional
              ? `/plans?open=${best.plan.plan_id}&day=${best.day}`
              : `/read?b=${best.book}&c=${best.chapter}&plan=${best.plan.plan_id}&day=${best.day}`;
            try {
              if ("serviceWorker" in navigator) {
                const reg = await navigator.serviceWorker.ready;
                await reg.showNotification(title, {
                  body,
                  icon: "/icon-app.jpg",
                  badge: "/icon-app.jpg",
                  tag: "faithcycle-daily",
                  data: { url, tag: "faithcycle-daily" },
                });
              } else {
                new Notification(title, { body, icon: "/icon-app.jpg" });
              }
            } catch {}
            localStorage.setItem(key, today);
          }
        }
      } catch {}
    })();
  }, [notifPerm, lang]);

  const enablePush = async () => {
    if (!isPushSupported()) return;
    try {
      setSubscribing(true);
      await subscribeToPush();
      setPushSubscribed(true);
      setNotifPerm("granted");
    } catch (e) {
      setNotifPerm(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
    } finally {
      setSubscribing(false);
    }
  };

  const bookLabel = votd?.book_name || getEnglishBookName(votd?.book);

  return (
    <div className="space-y-10">
      <div>
        <p className="uppercase tracking-[0.3em] text-xs text-[var(--text-secondary)]">
          {lang === "es" ? "Buenos días" : "Good morning"}
        </p>
        <h1 className="font-serif text-4xl md:text-5xl mt-2 tracking-tight" data-testid="home-greeting">
          {user?.name?.split(" ")[0] || (lang === "es" ? "Amigo" : "Friend")}.
        </h1>
      </div>

      {/* Daily reading reminder banner */}
      {todayReading && (
        <button
          data-testid="today-reminder-banner"
          onClick={() => {
            if (todayReading.plan.is_devotional) {
              // Devotional plans → open the day inside /plans (modal auto-opens via query params)
              navigate(`/plans?open=${todayReading.plan.plan_id}&day=${todayReading.day}`);
            } else {
              navigate(`/read?b=${todayReading.book}&c=${todayReading.chapter}&plan=${todayReading.plan.plan_id}&day=${todayReading.day}`);
            }
          }}
          className="w-full text-left rounded-3xl border border-[var(--accent)]/40 bg-[var(--highlight)]/40 p-6 flex items-center gap-5 group hover:bg-[var(--highlight)]/60 transition-colors duration-200"
        >
          <span className="w-11 h-11 rounded-full bg-[var(--bg)] border border-[var(--accent)]/40 flex items-center justify-center flex-shrink-0">
            <CalendarDays className="w-5 h-5 text-[var(--accent)]" strokeWidth={1.5} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--accent)]">
              {lang === "es" ? "Tu lectura de hoy" : "Today's reading"}
            </p>
            <p className="mt-1 font-serif text-xl md:text-2xl tracking-tight truncate">
              {lang === "es" ? "Día" : "Day"} {todayReading.day} · {lang === "es" ? todayReading.plan.name_es : todayReading.plan.name_en}
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-[var(--text-secondary)] group-hover:translate-x-1 transition-transform duration-200 flex-shrink-0" strokeWidth={1.5} />
        </button>
      )}

      {/* Notification opt-in — real Web Push (community posts, prayers, daily reading) */}
      {isPushSupported() && !pushSubscribed && notifPerm !== "denied" && (
        <button
          data-testid="enable-notif-btn"
          onClick={enablePush}
          disabled={subscribing}
          className="w-full text-left rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)] p-4 flex items-center gap-3 hover:bg-[var(--surface)] transition-colors duration-200 disabled:opacity-60"
        >
          <Bell className="w-4 h-4 text-[var(--accent)]" strokeWidth={1.5} />
          <div className="flex-1">
            <p className="text-sm">
              {lang === "es"
                ? (subscribing ? "Activando notificaciones…" : "Activa notificaciones push")
                : (subscribing ? "Enabling notifications…" : "Enable push notifications")}
            </p>
            <p className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)] mt-0.5">
              {lang === "es"
                ? "Comunidad, oraciones y tu lectura diaria"
                : "Community, prayers, and your daily reading"}
            </p>
          </div>
        </button>
      )}
      {isPushSupported() && pushSubscribed && (
        <div className="text-xs text-[var(--text-secondary)] flex items-center gap-2" data-testid="push-active-indicator">
          <Bell className="w-3 h-3 text-[var(--accent)]" strokeWidth={1.5} />
          {lang === "es" ? "Notificaciones activas" : "Notifications active"}
        </div>
      )}
      {notifPerm === "denied" && (
        <p className="text-xs text-[var(--text-secondary)] flex items-center gap-2">
          <BellOff className="w-3 h-3" strokeWidth={1.5} />
          {lang === "es"
            ? "Notificaciones bloqueadas. Actívalas desde los permisos de tu navegador."
            : "Notifications blocked. Enable them from your browser permissions."}
        </p>
      )}

      <InstallBanner />

      {/* Verse of the Day - Hero */}
      <section
        data-testid="votd-card"
        className="relative rounded-3xl overflow-hidden border border-[var(--border)] group cursor-pointer"
        onClick={() => votd && navigate(`/read?b=${votd.book}&c=${votd.chapter}&v=${votd.verse}`)}
      >
        <img
          src={VOTD_IMG}
          alt=""
          className="w-full h-[420px] md:h-[500px] object-cover transition-transform duration-700 group-hover:scale-105"
          onError={(e) => {
            // Unsplash occasionally removes photos → fall back to a known-good image so the card is never empty
            if (!e.currentTarget.dataset.fallback) {
              e.currentTarget.dataset.fallback = "1";
              e.currentTarget.src = "https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?auto=format&fit=crop&w=1600&q=85";
            }
          }}
        />
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="absolute inset-0 flex flex-col justify-between p-8 md:p-12">
          <p className="uppercase tracking-[0.3em] text-sm md:text-base text-white/90 font-medium">{t("votd", lang)}</p>
          <div className="max-w-2xl">
            {loading ? (
              <Skeleton className="h-24 w-full bg-white/10" />
            ) : (
              <>
                <p
                  className="font-serif text-white leading-[1.15] tracking-tight"
                  style={{ fontSize: "19px" }}
                  data-testid="votd-verse"
                >
                  "{votd?.text}"
                </p>
                <p className="mt-6 uppercase tracking-[0.25em] text-base text-white/90 font-medium">
                  {bookLabel} {votd?.chapter}:{votd?.verse} · {version}
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      <button
        data-testid="devotional-home-card"
        onClick={() => navigate("/devocional")}
        className="w-full text-left rounded-3xl border border-[var(--border)] bg-[var(--surface)] p-6 md:p-8 flex items-center gap-5 group hover:bg-[var(--surface-elevated)] transition-colors duration-200"
      >
        <span className="w-12 h-12 rounded-full bg-[var(--highlight)]/60 border border-[var(--accent)]/40 flex items-center justify-center flex-shrink-0">
          <Headphones className="w-5 h-5 text-[var(--accent)]" strokeWidth={1.5} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--accent)]">
            {lang === "es" ? "Devocional diario · 5-7 min" : "Daily devotional · 5-7 min"}
          </p>
          <p className="mt-1 font-serif text-xl md:text-2xl tracking-tight">
            {lang === "es" ? "Lectura, reflexión y oración de hoy" : "Today's reading, reflection & prayer"}
          </p>
        </div>
        <ArrowRight className="w-5 h-5 text-[var(--text-secondary)] group-hover:translate-x-1 transition-transform duration-200 flex-shrink-0" strokeWidth={1.5} />
      </button>

      {/* Quick tiles */}
      <section className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Tile
          testid="tile-read"
          onClick={() => navigate("/read")}
          image={READING_IMG}
          Icon={BookOpen}
          title={lang === "es" ? "Continuar leyendo" : "Continue reading"}
          desc={lang === "es" ? "Lectura sin distracciones. Elige libro y capítulo." : "Distraction-free reader. Pick your book & chapter."}
        />
        <Tile
          testid="tile-community"
          onClick={() => navigate("/community")}
          image={COMMUNITY_IMG}
          Icon={MessageCircle}
          title={lang === "es" ? "Comunidad" : "Community"}
          desc={lang === "es" ? "Comparte reflexiones y comenta en versículos." : "Share reflections and discuss verses together."}
        />
        <Tile
          testid="tile-plans"
          onClick={() => navigate("/plans")}
          image={PLANS_IMG}
          Icon={CalendarDays}
          title={lang === "es" ? "Planes de lectura" : "Reading plans"}
          desc={planList.length
            ? (lang === "es" ? `${planList.length} planes para leer paso a paso.` : `${planList.length} plans to read step by step.`)
            : (lang === "es" ? "Planes cortos para leer paso a paso." : "Short plans to read step by step.")}
        />
      </section>
    </div>
  );
}

function Tile({ testid, onClick, image, Icon, title, desc, children }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="text-left group rounded-3xl border border-[var(--border)] bg-[var(--surface)] overflow-hidden transition-colors duration-300 hover:bg-[var(--surface-elevated)] flex flex-col justify-start"
    >
      {image ? (
        <div className="h-40 w-full overflow-hidden flex-shrink-0">
          <img src={image} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        </div>
      ) : (
        <div className="h-40 flex items-center justify-center bg-[var(--highlight)]/40">
          <Icon className="w-10 h-10 text-[var(--accent)]" strokeWidth={1.2} />
        </div>
      )}
      <div className="p-6 w-full">
        <div className="flex items-center justify-between">
          <Icon className="w-5 h-5 text-[var(--accent)]" strokeWidth={1.5} />
          <ArrowRight className="w-4 h-4 text-[var(--text-secondary)] group-hover:translate-x-1 transition-transform duration-300" strokeWidth={1.5} />
        </div>
        <h3 className="font-serif text-2xl mt-4 tracking-tight">{title}</h3>
        <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">{desc}</p>
        {children}
      </div>
    </button>
  );
}

const PLANS_IMG = "https://images.unsplash.com/photo-1594100513700-659fa01ce822?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

const EN_BOOKS = ["Genesis","Exodus","Leviticus","Numbers","Deuteronomy","Joshua","Judges","Ruth","1 Samuel","2 Samuel","1 Kings","2 Kings","1 Chronicles","2 Chronicles","Ezra","Nehemiah","Esther","Job","Psalms","Proverbs","Ecclesiastes","Song of Solomon","Isaiah","Jeremiah","Lamentations","Ezekiel","Daniel","Hosea","Joel","Amos","Obadiah","Jonah","Micah","Nahum","Habakkuk","Zephaniah","Haggai","Zechariah","Malachi","Matthew","Mark","Luke","John","Acts","Romans","1 Corinthians","2 Corinthians","Galatians","Ephesians","Philippians","Colossians","1 Thessalonians","2 Thessalonians","1 Timothy","2 Timothy","Titus","Philemon","Hebrews","James","1 Peter","2 Peter","1 John","2 John","3 John","Jude","Revelation"];
export function getEnglishBookName(id) { return id ? EN_BOOKS[id - 1] : ""; }
